import { useEffect, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/base/SEOHead";

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于非麻瓜漫画",
  description: "非麻瓜漫画品牌介绍、用户口碑及联系方式",
  url: "https://feimagua.com/about",
  mainEntity: {
    "@type": "Organization",
    name: "非麻瓜漫画",
    url: "https://feimagua.com",
    description: "专业的免费韩漫在线阅读平台",
    foundingDate: "2024",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "contact@feimagua.com",
      availableLanguage: ["Chinese"],
    },
  },
};

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  return (
    <>
      <SEOHead
        title="关于我们 - 非麻瓜漫画_品牌介绍与联系方式"
        description="非麻瓜漫画是专业的免费韩漫在线阅读平台。了解我们的品牌故事、用户口碑以及联系方式，我们致力于提供最优质的无广告韩漫阅读体验。"
        keywords="非麻瓜漫画,关于我们,品牌介绍,联系方式,韩漫平台"
        canonicalUrl="https://feimagua.com/about"
        schema={aboutSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: "关于我们" },
            ]}
          />

          <section className="max-w-4xl mx-auto px-4 md:px-6 py-10">
            <div className="text-center mb-12">
              <span className="text-4xl font-display text-primary-500 block mb-4">
                非麻瓜漫画
              </span>
              <h1 className="text-2xl md:text-3xl font-display text-foreground-950">
                关于我们
              </h1>
              <p className="text-sm text-foreground-500 mt-3 max-w-2xl mx-auto">
                免费韩漫在线阅读网 — 让每一位漫画爱好者都能享受纯粹的阅读乐趣
              </p>
            </div>

            <div className="space-y-12">
              <div>
                <h2 className="text-xl font-display text-foreground-950 mb-4 flex items-center gap-2">
                  <span className="w-1 h-6 bg-primary-500 rounded-full inline-block"></span>
                  品牌故事
                </h2>
                <div className="bg-background-50 rounded-xl p-6 space-y-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    非麻瓜漫画创立于2024年，由一群热爱韩国漫画的资深读者和技术极客共同打造。
                    我们的创始团队深感市面上韩漫阅读平台的种种痛点——广告泛滥、翻页卡顿、更新缓慢、
                    收费昂贵——于是决定亲手打造一个真正以读者体验为核心的漫画阅读平台。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    「非麻瓜」这个名字来源于创始人之间的一个玩笑：在漫画的世界里，「麻瓜」指的是那些看不懂、
                    不会欣赏漫画的人，而「非麻瓜」则代表我们是真正懂漫画、爱漫画的一群人。我们希望每一个来到
                    非麻瓜漫画的读者，都能找到自己喜欢的作品，享受最纯粹的阅读体验。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    经过两年的发展，非麻瓜漫画已经成为国内领先的免费韩漫在线阅读平台之一。
                    我们拥有超过2000部正版授权的韩国漫画资源，每日更新超过50话新内容，
                    累计服务超过500万漫画爱好者。我们的使命始终不变：让阅读变得更加简单、流畅和快乐。
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-display text-foreground-950 mb-4 flex items-center gap-2">
                  <span className="w-1 h-6 bg-accent-500 rounded-full inline-block"></span>
                  核心优势
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    {
                      icon: "ri-thumb-up-line",
                      title: "完全免费",
                      desc: "无需注册、无需付费，全部漫画资源永久免费开放阅读。我们不设置任何付费墙，不搞会员制度。",
                    },
                    {
                      icon: "ri-flashlight-line",
                      title: "即时更新",
                      desc: "专业更新团队7×24小时跟踪韩国各大平台动态，第一时间同步最新漫画章节。",
                    },
                    {
                      icon: "ri-scroll-to-bottom-line",
                      title: "下拉式观看",
                      desc: "创新的无缝下拉式阅读模式，无需翻页等待，流畅的沉浸式阅读体验。",
                    },
                    {
                      icon: "ri-shield-check-line",
                      title: "无广告干扰",
                      desc: "漫画正文零广告插入，保证阅读过程不会被任何广告打断，专注纯粹阅读。",
                    },
                    {
                      icon: "ri-smartphone-line",
                      title: "全平台适配",
                      desc: "完美适配手机、平板、电脑等各类设备，随时随地享受阅读乐趣。",
                    },
                    {
                      icon: "ri-book-open-line",
                      title: "海量资源",
                      desc: "超过2000部正版韩国漫画，涵盖八大分类，满足各类型读者的阅读需求。",
                    },
                  ].map((item, idx) => (
                    <div
                      key={idx}
                      className="bg-background-50 rounded-xl p-5 hover:shadow-sm transition-shadow"
                    >
                      <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center mb-3">
                        <i className={`${item.icon} text-xl text-primary-500`}></i>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1.5">
                        {item.title}
                      </h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="text-xl font-display text-foreground-950 mb-4 flex items-center gap-2">
                  <span className="w-1 h-6 bg-secondary-500 rounded-full inline-block"></span>
                  用户口碑
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    {
                      name: "小棉花糖",
                      rating: 5,
                      text: "用了半年多了，真心推荐！更新速度超快，而且真的没有广告，阅读体验一级棒。下拉式阅读太舒服了，回不去了。",
                      date: "2026年7月",
                    },
                    {
                      name: "韩漫爱好者小王",
                      rating: 5,
                      text: "之前在其他平台看漫画总是被广告打断，换了非麻瓜之后体验提升了太多。分类很全，找漫画很方便。",
                      date: "2026年7月",
                    },
                    {
                      name: "星星点灯",
                      rating: 5,
                      text: "作为一个重度韩漫读者，非麻瓜漫画是我用过最好用的韩漫平台。资源全、更新快、零广告、免费，还有什么不满意的？",
                      date: "2026年6月",
                    },
                    {
                      name: "二次元少女橙子",
                      rating: 5,
                      text: "最爱恋爱分类的漫画，非麻瓜的资源太全了！推荐给我身边所有喜欢韩漫的朋友了，大家都在用～",
                      date: "2026年6月",
                    },
                    {
                      name: "懒懒的熊猫",
                      rating: 4,
                      text: "总体体验很好，漫画种类丰富。希望能增加搜索功能，有时候想找特定的漫画不太方便。期待越来越好！",
                      date: "2026年5月",
                    },
                    {
                      name: "风中的蒲公英",
                      rating: 5,
                      text: "下拉式观看模式真的太香了！手机上看漫画体验巨好，流畅得像刷朋友圈一样。非麻瓜，永远的神！",
                      date: "2026年5月",
                    },
                  ].map((review, idx) => (
                    <div key={idx} className="bg-background-50 rounded-xl p-5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-foreground-900">
                          {review.name}
                        </span>
                        <span className="text-xs text-foreground-400">{review.date}</span>
                      </div>
                      <div className="flex items-center gap-0.5 mb-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i
                            key={i}
                            className={`text-xs ${
                              i < review.rating
                                ? "ri-star-fill text-accent-500"
                                : "ri-star-line text-foreground-300"
                            }`}
                          ></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        「{review.text}」
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="text-xl font-display text-foreground-950 mb-4 flex items-center gap-2">
                  <span className="w-1 h-6 bg-primary-500 rounded-full inline-block"></span>
                  联系我们
                </h2>
                <div className="flex flex-col lg:flex-row gap-6">
                  <div className="flex-1 bg-background-50 rounded-xl p-6">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-mail-line text-primary-500"></i>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-foreground-900">电子邮箱</h3>
                          <p className="text-sm text-foreground-500">contact@feimagua.com</p>
                          <p className="text-xs text-foreground-400 mt-0.5">商务合作、版权咨询请发送邮件</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-wechat-line text-accent-500"></i>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-foreground-900">微信公众号</h3>
                          <p className="text-sm text-foreground-500">非麻瓜漫画</p>
                          <p className="text-xs text-foreground-400 mt-0.5">关注获取最新更新通知和独家福利</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-weibo-line text-secondary-500"></i>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-foreground-900">微博</h3>
                          <p className="text-sm text-foreground-500">@非麻瓜漫画官方</p>
                          <p className="text-xs text-foreground-400 mt-0.5">每日漫画推荐和精彩片段分享</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-map-pin-line text-primary-500"></i>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-foreground-900">办公地址</h3>
                          <p className="text-sm text-foreground-500">北京市朝阳区望京SOHO T2-1806</p>
                          <p className="text-xs text-foreground-400 mt-0.5">周一至周五 10:00-18:00</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="lg:w-[380px] bg-background-50 rounded-xl p-6">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-4">
                      给我们留言
                    </h3>
                    <ContactForm />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}

function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem("website_alt") as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus("success");
      setFormData({ name: "", email: "", message: "" });
      setTimeout(() => setFormStatus("idle"), 3000);
      return;
    }

    setFormStatus("submitting");
    setFormError("");

    try {
      const payload = new FormData(form);
      const response = await fetch("https://readdy.ai/api/form/d9p0kjosenbniolesne0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        setFormData({ name: "", email: "", message: "" });
        setTimeout(() => setFormStatus("idle"), 3000);
      } else {
        const msg = parsed?.meta?.message || parsed?.message || "提交失败，请稍后重试";
        setFormError(msg);
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请检查网络后重试");
      setFormStatus("error");
    }
  };

  return (
    <form data-readdy-form="" onSubmit={handleSubmit} className="space-y-3">
      <div>
        <input
          type="text"
          name="name"
          placeholder="您的称呼"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800 placeholder:text-foreground-400"
        />
      </div>
      <div>
        <input
          type="email"
          name="email"
          placeholder="您的邮箱"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800 placeholder:text-foreground-400"
        />
      </div>
      <div>
        <textarea
          name="message"
          placeholder="请输入您的留言（最多500字）"
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          required
          maxLength={500}
          rows={4}
          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800 placeholder:text-foreground-400 resize-none"
        />
        <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
      </div>
      <div className="honeypot-field">
        <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
      </div>
      <button
        type="submit"
        disabled={formStatus === "submitting"}
        className="w-full py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
      >
        {formStatus === "submitting" ? "提交中..." : "发送留言"}
      </button>

      {formStatus === "success" && (
        <p className="text-sm text-center text-green-600">留言发送成功！我们会尽快回复您。</p>
      )}
      {formStatus === "error" && (
        <p className="text-sm text-center text-red-500">{formError}</p>
      )}
    </form>
  );
}